import React, { useState, useEffect } from "react";
import { Seo } from "../components/Seo";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, auth } from "../lib/api";
import { TotalReachLogo } from "../components/Logo";
import { PublicNav } from "../components/PublicNav";
import { Target, ArrowRight, Loader2, CheckCircle2, ChevronLeft, Eye, EyeOff, Check, X as XIcon } from "lucide-react";

// Strong-password requirements — enforced client-side AND matched server-side.
const PW_RULES = [
  { key: "len", label: "at least 8 characters", test: (p) => p.length >= 8 },
  { key: "upper", label: "one uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { key: "lower", label: "one lowercase letter", test: (p) => /[a-z]/.test(p) },
  { key: "number", label: "one number", test: (p) => /\d/.test(p) },
  { key: "symbol", label: "one symbol (!@#$…)", test: (p) => /[^A-Za-z0-9]/.test(p) },
];

export function passwordIsStrong(pw) {
  return PW_RULES.every((r) => r.test(pw || ""));
}

const PasswordField = ({ value, onChange, placeholder, testid, showRules, autoComplete, fieldCls }) => {
  const [show, setShow] = React.useState(false);
  const rules = PW_RULES.map((r) => ({ ...r, ok: r.test(value || "") }));
  return (
    <div>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={onChange}
          className={`${fieldCls || ""} pr-10`}
          placeholder={placeholder || "••••••••"}
          required
          autoComplete={autoComplete || "current-password"}
          data-testid={testid}
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          data-testid={`${testid}-toggle`}
          aria-label={show ? "Hide password" : "Show password"}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-cyan-300"
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
      {showRules && (
        <ul className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1 text-[11px]" data-testid={`${testid}-rules`}>
          {rules.map((r) => (
            <li key={r.key} className={`inline-flex items-center gap-1 ${r.ok ? "text-emerald-300" : "text-slate-500"}`}>
              {r.ok ? <Check className="w-3 h-3" /> : <XIcon className="w-3 h-3" />} {r.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const MODES = {
  signin: { pill: "Sign in to console", title: "Welcome back", sub: "Manage campaigns, audiences, and creatives across every channel." },
  signup: { pill: "Create your account", title: "Sign up for TotalReach", sub: "Create your login — an administrator approves new accounts before first sign-in." },
  demo: { pill: "Book a demo", title: "Request demo access", sub: "Tell us who you are — once approved, your demo credentials arrive by email." },
  forgot: { pill: "Reset your password", title: "Forgot password", sub: "Enter your email — if it has an account, we'll send a reset link (valid 60 minutes)." },
  reset: { pill: "Choose a new password", title: "Reset password", sub: "Set a new password for your account." },
};

const field = "w-full bg-slate-900/60 border border-white/10 rounded-md px-4 py-3 text-white focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 focus:outline-none transition-colors";
const Label = ({ children }) => <label className="text-xs uppercase tracking-wider text-slate-400 font-medium block mb-1.5">{children}</label>;

export default function Login({ mode: initialMode = "signin" }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const resetToken = params.get("token") || "";
  const nextPath = (params.get("next") || "").startsWith("/") ? params.get("next") : "";
  const promoCode = params.get("promo") || "";
  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState({ name: "", email: "", organization: "", phone: "", notes: "", password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const switchMode = (m) => { setMode(m); setError(""); setDone(null); };

  // Keep local `mode` in sync when the route prop changes — the same Login
  // component instance is reused across /login, /signup, /request-demo and
  // /reset-password, so without this the URL flips but the form stays stuck
  // on whatever mode was first mounted.
  useEffect(() => {
    setMode(initialMode);
    setError("");
    setDone(null);
  }, [initialMode]);

  // Auto-suppress the floating "Book a Demo" pill for the rest of the session
  // once the visitor lands on the demo request screen — no more nagging.
  useEffect(() => {
    if (mode === "demo") {
      try {
        sessionStorage.setItem("tr_demo_visited", "1");
      } catch {
        /* no-op */
      }
    }
  }, [mode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    // Enforce strong password on any account-creating or password-changing flow.
    if ((mode === "signup" || mode === "reset") && !passwordIsStrong(form.password)) {
      setError("Password must have upper + lower case, a number, a symbol, and be at least 8 characters.");
      setLoading(false);
      return;
    }
    try {
      if (mode === "signin") {
        const { data } = await api.post("/auth/login", { email: form.email, password: form.password });
        auth.login(data.token, data.user);
        navigate(`/select-campaign?next=${encodeURIComponent(nextPath || "/dashboard")}`);
      } else if (mode === "forgot") {
        const { data } = await api.post("/public/auth/forgot-password", { email: form.email });
        setDone(data.message);
      } else if (mode === "reset") {
        if (form.password !== form.confirm) { setError("Passwords don't match"); setLoading(false); return; }
        const { data } = await api.post("/public/auth/reset-password", { token: resetToken, new_password: form.password });
        setDone(data.message);
      } else {
        const { data } = await api.post("/public/access/request", {
          type: mode === "demo" ? "demo" : "signup",
          name: form.name, email: form.email, organization: form.organization,
          phone: form.phone, notes: form.notes,
          password: mode === "signup" ? form.password : undefined,
          promo_code: mode === "signup" && promoCode ? promoCode : undefined,
        });
        setDone(data.message);
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x?.msg || "").join(" ") : "Something went wrong — try again");
    } finally {
      setLoading(false);
    }
  };

  const m = MODES[mode];

  return (
    <div className="min-h-screen bg-app flex flex-col" data-testid="auth-page">
      {mode === "demo" ? <Seo title="Book a demo" description="See the TotalReach Political Campaign OS live — CTV advertising, fundraising and race intelligence walkthrough for your 2026 race." path="/request-demo" />
        : mode === "signup" ? <Seo title="Sign up" description="Create your TotalReach account and run your 2026 campaign's advertising, fundraising and compliance in one place." path="/signup" />
        : <Seo title={mode === "reset" ? "Reset password" : "Log in"} noindex />}
      <PublicNav compact />
      <div className="flex-1 flex">
      {/* Left side - branding */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgb(var(--ok-400)_/_0.14),transparent_55%),radial-gradient(ellipse_at_bottom_right,rgb(var(--brand-400)_/_0.12),transparent_50%)]" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <button onClick={() => navigate("/")} className="w-fit" data-testid="auth-logo-home"><TotalReachLogo size={34} /></button>
          <div className="max-w-lg">
            <div className="text-xs uppercase tracking-[0.3em] text-cyan-300 mb-4 font-medium">
              The Unified Marketing OS
            </div>
            <h1 className="font-display text-5xl xl:text-6xl font-bold leading-[1.05] tracking-tight">
              The Unified Marketing OS for<br />
              <span className="brand-gradient">Political Campaigns</span>
            </h1>
            <p className="mt-6 text-slate-300 text-lg leading-relaxed max-w-md">
              Reach voters wherever they are — premium websites, connected TV, social media,
              Google campaigns, digital out-of-home, and unified reporting.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
              {[
                { v: "20+", l: "Channels" },
                { v: "CTV", l: "Hero product" },
                { v: "AI", l: "Creative studio" },
              ].map((s) => (
                <div key={s.l} className="glass-card rounded-lg p-3">
                  <div className="text-2xl font-display font-bold brand-gradient">{s.v}</div>
                  <div className="text-xs text-slate-400">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="text-xs text-slate-500">© 2026 TotalReach.us — Built with compliance-first architecture</div>
        </div>
      </div>

      {/* Right - form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <button onClick={() => navigate("/")}><TotalReachLogo size={34} /></button>
          </div>

          <button onClick={() => navigate("/")} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-cyan-300 mb-5" data-testid="auth-back-home">
            <ChevronLeft className="w-3.5 h-3.5" /> Back to totalreach.us
          </button>

          {done ? (
            <div data-testid="auth-success">
              <div className="w-14 h-14 rounded-full bg-emerald-400/10 border border-emerald-400/30 flex items-center justify-center mb-5">
                <CheckCircle2 className="w-7 h-7 text-emerald-300" />
              </div>
              <h2 className="font-display text-3xl font-bold mb-3 tracking-tight">{mode === "forgot" ? "Check your email" : mode === "reset" ? "Password updated" : "Request received"}</h2>
              <p className="text-slate-300 leading-relaxed">{done}</p>
              <button onClick={() => { switchMode("signin"); navigate("/login"); }} data-testid="success-goto-login"
                className="mt-8 w-full border border-white/15 text-white rounded-full px-6 py-3 hover:border-cyan-400/50 hover:text-cyan-300 transition-colors">
                Go to sign in
              </button>
            </div>
          ) : (
            <>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-400/10 border border-cyan-400/30 mb-6">
                <Target className="w-3.5 h-3.5 text-cyan-300" />
                <span className="text-xs text-cyan-300 font-medium uppercase tracking-wider">{m.pill}</span>
              </div>

              <h2 className="font-display text-4xl font-bold mb-2 tracking-tight">{m.title}</h2>
              <p className="text-slate-400 mb-8">{m.sub}</p>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid={`auth-form-${mode}`}>
                {(mode === "signup" || mode === "demo") && (
                  <div>
                    <Label>Full name</Label>
                    <input value={form.name} onChange={set("name")} className={field} placeholder="Jane Smith" required data-testid="auth-name-input" />
                  </div>
                )}
                {mode !== "reset" && (
                  <div>
                    <Label>Email</Label>
                    <input type="email" value={form.email} onChange={set("email")} className={field} placeholder="you@campaign.com" required data-testid="login-email-input" />
                  </div>
                )}
                {(mode === "signup" || mode === "demo") && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Organization</Label>
                      <input value={form.organization} onChange={set("organization")} className={field} placeholder="Committee / agency" data-testid="auth-org-input" />
                    </div>
                    <div>
                      <Label>Phone (optional)</Label>
                      <input value={form.phone} onChange={set("phone")} className={field} placeholder="+1 …" data-testid="auth-phone-input" />
                    </div>
                  </div>
                )}
                {mode === "demo" && (
                  <div>
                    <Label>What would you like to see? (optional)</Label>
                    <textarea value={form.notes} onChange={set("notes")} rows={3} className={field} placeholder="CTV campaigns, fundraising, voter targeting…" data-testid="auth-notes-input" />
                  </div>
                )}
                {(mode === "signin" || mode === "signup" || mode === "reset") && (
                  <div>
                    <Label>{mode === "signup" ? "Choose a password" : mode === "reset" ? "New password" : "Password"}</Label>
                    <PasswordField
                      value={form.password}
                      onChange={set("password")}
                      testid="login-password-input"
                      fieldCls={field}
                      showRules={mode === "signup" || mode === "reset"}
                      autoComplete={mode === "signin" ? "current-password" : "new-password"}
                    />
                  </div>
                )}
                {mode === "reset" && (
                  <div>
                    <Label>Confirm new password</Label>
                    <PasswordField
                      value={form.confirm}
                      onChange={set("confirm")}
                      testid="reset-confirm-input"
                      fieldCls={field}
                      autoComplete="new-password"
                    />
                  </div>
                )}
                {mode === "signin" && (
                  <div className="text-right -mt-1">
                    <button type="button" onClick={() => switchMode("forgot")} className="text-xs text-slate-400 hover:text-cyan-300" data-testid="forgot-password-link">
                      Forgot password?
                    </button>
                  </div>
                )}

                {error && (
                  <div className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-md p-3" data-testid="login-error">
                    {error}
                  </div>
                )}

                <button type="submit" disabled={loading} data-testid="login-submit-button"
                  className="w-full bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 font-semibold rounded-full px-6 py-3 hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-50">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                    <>{mode === "signin" ? "Sign in" : mode === "signup" ? "Create account" : mode === "demo" ? "Request demo" : mode === "forgot" ? "Send reset link" : "Update password"} <ArrowRight className="w-4 h-4" /></>
                  )}
                </button>
              </form>

              <div className="mt-8 text-sm text-slate-400 space-y-2">
                {mode !== "signin" && (
                  <div>Already have access? <button onClick={() => switchMode("signin")} className="text-cyan-300 hover:underline" data-testid="switch-signin">Sign in</button></div>
                )}
                {mode !== "signup" && mode !== "reset" && (
                  <div>Ready to set up your own account? <button onClick={() => switchMode("signup")} className="text-cyan-300 hover:underline" data-testid="switch-signup">Sign up</button></div>
                )}
                {mode !== "demo" && mode !== "reset" && (
                  <div>Just exploring? <button onClick={() => switchMode("demo")} className="text-cyan-300 hover:underline" data-testid="switch-demo">Book a demo account</button></div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}
